// src/pages/InstallationModule.js
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AuthContext from "../context/AuthContext";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_EVIDENCES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png"]);

function localDateInEcuador() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Guayaquil",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function localTimeInEcuador() {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/Guayaquil",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());
}

function emptyCheck() {
  return {
    parameter_name: "",
    unit: "",
    reference_value: "",
    measured_value: "",
    tolerance: "",
    observations: "",
  };
}

function buildInitialForm(latitude = "", longitude = "") {
  return {
    location: "",
    event_date: localDateInEcuador(),
    event_time: localTimeInEcuador(),
    responsible_people: "",
    reason: "",
    observations: "",
    latitude: latitude || "",
    longitude: longitude || "",
  };
}

function parseNumberOrNull(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function calculateCheck(check) {
  const reference = parseNumberOrNull(check.reference_value);
  const measured = parseNumberOrNull(check.measured_value);
  const tolerance = parseNumberOrNull(check.tolerance);

  const error =
    reference !== null && measured !== null ? measured - reference : null;
  const compliance =
    error !== null && tolerance !== null
      ? Math.abs(error) <= Math.abs(tolerance)
      : null;

  return { error, compliance };
}

function formatNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";

  return new Intl.NumberFormat("es-EC", {
    maximumFractionDigits: 6,
  }).format(number);
}

function formatDate(value) {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const [year, month, day] = raw.split("-");
  return year && month && day ? `${day}/${month}/${year}` : raw;
}

function formatTime(value) {
  if (!value) return "—";
  return String(value).slice(0, 5);
}

function resolvePublicUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;

  const configuredBase = String(process.env.REACT_APP_BACKEND_URL || "")
    .trim()
    .replace(/\/$/, "");
  const publicBase = configuredBase.replace(/\/api$/i, "");

  return `${publicBase}${raw.startsWith("/") ? "" : "/"}${raw}`;
}

function eventLabel(eventType) {
  return eventType === "uninstallation" ? "Desinstalación" : "Instalación";
}

function statusLabel(status) {
  const labels = {
    completed: "Completado",
    draft: "Borrador",
    cancelled: "Cancelado",
  };
  return labels[status] || status || "—";
}

export default function InstallationModule() {
  const navigate = useNavigate();
  const { sensorId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const sensorName = searchParams.get("sensorName") || sensorId;
  const initialLatitude = searchParams.get("latitude") || "";
  const initialLongitude = searchParams.get("longitude") || "";
  const sensorType = searchParams.get("tipo") || "";

  const canManageInstallations = !!user?.permissions?.includes(
    "manage_installations"
  );

  const [activeTab, setActiveTab] = useState("installation");
  const [form, setForm] = useState(() =>
    buildInitialForm(initialLatitude, initialLongitude)
  );
  const [checks, setChecks] = useState([emptyCheck()]);
  const [evidences, setEvidences] = useState([]);
  const [formError, setFormError] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);

  const eventType =
    activeTab === "uninstallation" ? "uninstallation" : "installation";

  const clearEvidencePreviews = useCallback((items) => {
    (items || []).forEach((item) => {
      if (item?.preview) URL.revokeObjectURL(item.preview);
    });
  }, []);

  useEffect(() => {
    return () => clearEvidencePreviews(evidences);
  }, [evidences, clearEvidencePreviews]);

  const loadHistory = useCallback(async () => {
    if (!token || !sensorId || !canManageInstallations) return;

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const { data } = await axios.get(`${API_ROOT}/installations`, {
        ...authHeader,
        params: { sensor_id: sensorId },
      });
      setHistory(Array.isArray(data?.events) ? data.events : []);
    } catch (error) {
      setHistoryError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo cargar el historial."
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [authHeader, canManageInstallations, sensorId, token]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const latestInstallation = useMemo(
    () => history.find((item) => item.event_type === "installation") || null,
    [history]
  );

  const latestUninstallation = useMemo(
    () => history.find((item) => item.event_type === "uninstallation") || null,
    [history]
  );

  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setFormError("");
    setFormMessage("");
  }

  function updateCheck(index, field, value) {
    setChecks((current) =>
      current.map((check, checkIndex) =>
        checkIndex === index ? { ...check, [field]: value } : check
      )
    );
    setFormError("");
    setFormMessage("");
  }

  function addCheck() {
    setChecks((current) => [...current, emptyCheck()]);
  }

  function removeCheck(index) {
    setChecks((current) => current.filter((_, checkIndex) => checkIndex !== index));
  }

  function handleEvidenceSelection(event) {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    setFormError("");
    setFormMessage("");

    if (!selectedFiles.length) return;

    const availableSlots = MAX_EVIDENCES - evidences.length;
    if (availableSlots <= 0) {
      setFormError(`Solo se permiten ${MAX_EVIDENCES} evidencias por evento.`);
      return;
    }

    const accepted = [];
    const rejected = [];

    selectedFiles.slice(0, availableSlots).forEach((file) => {
      if (!ALLOWED_TYPES.has(file.type)) {
        rejected.push(`${file.name}: formato no permitido`);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        rejected.push(`${file.name}: supera 5 MB`);
        return;
      }

      accepted.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        description: "",
      });
    });

    setEvidences((current) => [...current, ...accepted]);

    if (selectedFiles.length > availableSlots) {
      rejected.push(`Solo se agregaron ${availableSlots} archivos por el límite de 10.`);
    }

    if (rejected.length) {
      setFormError(rejected.join(". "));
    }
  }

  function removeEvidence(index) {
    setEvidences((current) => {
      const target = current[index];
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return current.filter((_, evidenceIndex) => evidenceIndex !== index);
    });
  }

  function updateEvidenceDescription(index, value) {
    setEvidences((current) =>
      current.map((item, evidenceIndex) =>
        evidenceIndex === index ? { ...item, description: value } : item
      )
    );
  }

  function validateForm() {
    if (!form.location.trim()) return "La ubicación es obligatoria.";
    if (!form.event_date) return "La fecha es obligatoria.";
    if (!form.event_time) return "La hora es obligatoria.";
    if (!form.responsible_people.trim()) return "Los encargados son obligatorios.";
    if (eventType === "uninstallation" && !form.reason.trim()) {
      return "El motivo de desinstalación es obligatorio.";
    }

    const invalidCheck = checks.find(
      (check) =>
        check.parameter_name.trim() === "" &&
        [
          check.unit,
          check.reference_value,
          check.measured_value,
          check.tolerance,
          check.observations,
        ].some((value) => String(value || "").trim() !== "")
    );

    if (invalidCheck) {
      return "Toda comprobación con datos debe tener un parámetro.";
    }

    return "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError("");
    setFormMessage("");

    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const normalizedChecks = checks
      .filter((check) => check.parameter_name.trim())
      .map((check, index) => ({
        parameter_name: check.parameter_name.trim(),
        unit: check.unit.trim(),
        reference_value: parseNumberOrNull(check.reference_value),
        measured_value: parseNumberOrNull(check.measured_value),
        tolerance: parseNumberOrNull(check.tolerance),
        observations: check.observations.trim(),
        sort_order: index,
      }));

    const payload = new FormData();
    payload.append("sensor_id", sensorId);
    payload.append("event_type", eventType);
    payload.append("location", form.location.trim());
    payload.append("event_date", form.event_date);
    payload.append("event_time", form.event_time);
    payload.append("responsible_people", form.responsible_people.trim());
    payload.append("reason", eventType === "uninstallation" ? form.reason.trim() : "");
    payload.append("observations", form.observations.trim());
    payload.append("latitude", form.latitude);
    payload.append("longitude", form.longitude);
    payload.append("checks", JSON.stringify(normalizedChecks));
    payload.append(
      "evidence_descriptions",
      JSON.stringify(evidences.map((item) => item.description.trim()))
    );

    evidences.forEach((item) => payload.append("evidences", item.file));

    setSaving(true);

    try {
      const { data } = await axios.post(`${API_ROOT}/installations`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setFormMessage(data?.message || "Registro guardado correctamente.");
      clearEvidencePreviews(evidences);
      setEvidences([]);
      setChecks([emptyCheck()]);
      setForm(buildInitialForm(initialLatitude, initialLongitude));
      await loadHistory();
      setActiveTab("history");
    } catch (error) {
      setFormError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "No se pudo guardar el registro."
      );
    } finally {
      setSaving(false);
    }
  }

  async function openDetail(eventId) {
    setDetailLoading(true);
    setHistoryError("");

    try {
      const { data } = await axios.get(
        `${API_ROOT}/installations/${encodeURIComponent(eventId)}`,
        authHeader
      );
      setDetail(data?.event || null);
    } catch (error) {
      setHistoryError(
        error?.response?.data?.error || "No se pudo obtener el detalle."
      );
    } finally {
      setDetailLoading(false);
    }
  }

  async function regeneratePdf(eventId) {
    setRegeneratingId(eventId);
    setHistoryError("");

    try {
      await axios.post(
        `${API_ROOT}/installations/${encodeURIComponent(eventId)}/regenerate-pdf`,
        {},
        authHeader
      );
      await loadHistory();
      if (detail?.id === eventId) await openDetail(eventId);
    } catch (error) {
      setHistoryError(
        error?.response?.data?.error || "No se pudo regenerar el PDF."
      );
    } finally {
      setRegeneratingId(null);
    }
  }

  const styles = (
    <style>{`
      .installation-page{min-height:100vh;background:#f4f7fb;color:#0f172a;padding:24px;font-family:ui-sans-serif,system-ui,-apple-system,"Segoe UI",Roboto,Arial}
      .installation-shell{max-width:1240px;margin:0 auto}
      .installation-header{display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;margin-bottom:18px}
      .installation-header h1{margin:0;font-size:clamp(26px,3vw,40px);color:#174ea6;font-weight:950}
      .installation-subtitle{margin:6px 0 0;color:#64748b;font-weight:700}
      .installation-back{border:0;background:#0f172a;color:#fff;border-radius:10px;padding:10px 14px;font-weight:850;cursor:pointer}
      .installation-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:18px}
      .installation-summary-card{background:#fff;border:1px solid #dbe4f0;border-radius:14px;padding:15px;box-shadow:0 5px 18px rgba(15,23,42,.06)}
      .installation-summary-card span{display:block;color:#64748b;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.04em}
      .installation-summary-card strong{display:block;margin-top:6px;font-size:16px;color:#0f172a;overflow-wrap:anywhere}
      .installation-tabs{display:flex;gap:8px;flex-wrap:wrap;background:#eaf2ff;border:1px solid #dbeafe;border-radius:14px;padding:8px;margin-bottom:18px}
      .installation-tab{border:0;background:transparent;color:#334155;padding:10px 16px;border-radius:10px;font-weight:900;cursor:pointer}
      .installation-tab.active{background:#174ea6;color:#fff;box-shadow:0 4px 12px rgba(23,78,166,.22)}
      .installation-card{background:#fff;border:1px solid #dbe4f0;border-radius:16px;padding:18px;box-shadow:0 8px 24px rgba(15,23,42,.07);margin-bottom:18px}
      .installation-card h2{margin:0 0 6px;color:#0f172a;font-size:21px}
      .installation-card-note{margin:0 0 16px;color:#64748b;font-weight:650}
      .installation-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px}
      .installation-field{display:grid;gap:6px}
      .installation-field.full{grid-column:1/-1}
      .installation-field label{font-size:13px;font-weight:850;color:#334155}
      .installation-input,.installation-textarea{width:100%;box-sizing:border-box;border:1px solid #cbd5e1;border-radius:10px;padding:11px 12px;font:inherit;color:#0f172a;background:#fff;outline:none}
      .installation-input:focus,.installation-textarea:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.12)}
      .installation-textarea{min-height:92px;resize:vertical}
      .installation-section-title{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin:22px 0 10px}
      .installation-section-title h3{margin:0;font-size:17px;color:#174ea6}
      .installation-secondary-button{border:1px solid #bfdbfe;background:#eff6ff;color:#1d4ed8;border-radius:9px;padding:9px 12px;font-weight:850;cursor:pointer}
      .installation-check-wrap{overflow-x:auto;border:1px solid #dbe4f0;border-radius:12px}
      .installation-check-table{width:100%;min-width:1030px;border-collapse:collapse}
      .installation-check-table th,.installation-check-table td{padding:9px;border-bottom:1px solid #e2e8f0;text-align:left;vertical-align:top}
      .installation-check-table th{background:#f1f5f9;font-size:12px;color:#334155}
      .installation-check-table .installation-input{min-width:95px;padding:8px}
      .installation-result{font-weight:900;white-space:nowrap;padding-top:9px}
      .installation-result.ok{color:#166534}.installation-result.no{color:#b91c1c}.installation-result.pending{color:#64748b}
      .installation-remove{border:0;background:#fee2e2;color:#b91c1c;border-radius:8px;padding:8px 10px;font-weight:900;cursor:pointer}
      .installation-upload{display:block;border:2px dashed #93c5fd;background:#eff6ff;border-radius:12px;padding:18px;text-align:center;color:#1d4ed8;font-weight:900;cursor:pointer}
      .installation-upload input{display:none}
      .installation-evidence-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:14px;margin-top:14px}
      .installation-evidence-card{border:1px solid #dbe4f0;border-radius:12px;padding:10px;background:#fff}
      .installation-evidence-card img{display:block;width:100%;height:180px;object-fit:cover;border-radius:9px;background:#e2e8f0}
      .installation-evidence-card .installation-input{margin-top:9px}
      .installation-evidence-actions{display:flex;justify-content:space-between;gap:8px;align-items:center;margin-top:8px;color:#64748b;font-size:12px;font-weight:750}
      .installation-submit-row{display:flex;align-items:center;justify-content:flex-end;gap:12px;flex-wrap:wrap;margin-top:20px}
      .installation-submit{border:0;background:#16a34a;color:#fff;border-radius:10px;padding:12px 18px;font-weight:950;cursor:pointer}
      .installation-submit:disabled{opacity:.6;cursor:not-allowed}
      .installation-alert{border-radius:10px;padding:11px 13px;margin:12px 0;font-weight:750}
      .installation-alert.error{background:#fee2e2;color:#991b1b;border:1px solid #fecaca}
      .installation-alert.success{background:#dcfce7;color:#166534;border:1px solid #86efac}
      .installation-history-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:14px;margin-bottom:18px}
      .installation-state-card{border:1px solid #dbe4f0;border-radius:14px;padding:14px;background:#f8fafc}
      .installation-state-card h3{margin:0 0 8px;color:#174ea6}.installation-state-card p{margin:4px 0;color:#475569;font-weight:650}
      .installation-history-table-wrap{overflow-x:auto;border:1px solid #dbe4f0;border-radius:12px}
      .installation-history-table{width:100%;min-width:900px;border-collapse:collapse}
      .installation-history-table th,.installation-history-table td{padding:11px;border-bottom:1px solid #e2e8f0;text-align:left;font-size:13px}
      .installation-history-table th{background:#f1f5f9;color:#334155;font-weight:900}
      .installation-history-table td{color:#334155;font-weight:650}
      .installation-pill{display:inline-flex;border-radius:999px;padding:5px 9px;font-size:11px;font-weight:900;background:#e0e7ff;color:#3730a3}
      .installation-action-row{display:flex;gap:7px;flex-wrap:wrap}
      .installation-action{border:0;border-radius:8px;padding:7px 10px;font-weight:850;cursor:pointer;background:#eaf2ff;color:#174ea6;text-decoration:none;display:inline-flex;align-items:center}
      .installation-action.pdf{background:#dcfce7;color:#166534}.installation-action.warning{background:#fef3c7;color:#92400e}
      .installation-empty{text-align:center;color:#64748b;padding:28px;font-weight:800}
      .installation-modal{position:fixed;inset:0;background:rgba(2,6,23,.58);display:flex;align-items:center;justify-content:center;padding:18px;z-index:3000}
      .installation-modal-card{width:min(980px,96vw);max-height:92vh;overflow:auto;background:#fff;border-radius:16px;padding:18px;box-shadow:0 24px 70px rgba(2,6,23,.28)}
      .installation-modal-head{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:12px}
      .installation-modal-head h2{margin:0}.installation-close{border:0;border-radius:9px;background:#0f172a;color:#fff;padding:9px 12px;font-weight:900;cursor:pointer}
      .installation-detail-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-bottom:16px}
      .installation-detail-item{background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:10px}
      .installation-detail-item span{display:block;color:#64748b;font-size:11px;font-weight:850;text-transform:uppercase}.installation-detail-item strong{display:block;margin-top:4px;color:#0f172a;overflow-wrap:anywhere}
      .installation-detail-images{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
      .installation-detail-images figure{margin:0;border:1px solid #e2e8f0;border-radius:12px;padding:9px}.installation-detail-images img{width:100%;height:260px;object-fit:contain;background:#f1f5f9;border-radius:8px}.installation-detail-images figcaption{margin-top:7px;color:#475569;font-weight:700;text-align:center}
      @media(max-width:850px){.installation-summary,.installation-grid,.installation-history-grid,.installation-detail-grid{grid-template-columns:1fr}.installation-evidence-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
      @media(max-width:560px){.installation-page{padding:14px}.installation-evidence-grid,.installation-detail-images{grid-template-columns:1fr}.installation-header{align-items:flex-start}.installation-back{width:100%}}
    `}</style>
  );

  if (!canManageInstallations) {
    return (
      <div className="installation-page">
        {styles}
        <div className="installation-shell">
          <button className="installation-back" onClick={() => navigate(-1)}>
            Volver
          </button>
          <div className="installation-card" style={{ marginTop: 18 }}>
            <h2>Acceso restringido</h2>
            <p className="installation-card-note">
              Tu usuario no tiene el permiso <strong>manage_installations</strong>.
              Cierra sesión e ingresa nuevamente si el permiso fue asignado recientemente.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="installation-page">
      {styles}
      <div className="installation-shell">
        <header className="installation-header">
          <div>
            <h1>Instalación y desinstalación</h1>
            <p className="installation-subtitle">
              Registro técnico, comprobación metrológica, evidencias y PDF automático.
            </p>
          </div>
          <button className="installation-back" type="button" onClick={() => navigate(-1)}>
            ← Volver al panel
          </button>
        </header>

        <section className="installation-summary">
          <div className="installation-summary-card">
            <span>Equipo</span>
            <strong>{sensorName || "—"}</strong>
          </div>
          <div className="installation-summary-card">
            <span>ID del sensor</span>
            <strong>{sensorId || "—"}</strong>
          </div>
          <div className="installation-summary-card">
            <span>Tipo</span>
            <strong>{sensorType || "—"}</strong>
          </div>
        </section>

        <nav className="installation-tabs" aria-label="Secciones del módulo">
          <button
            type="button"
            className={`installation-tab ${activeTab === "installation" ? "active" : ""}`}
            onClick={() => setActiveTab("installation")}
          >
            Instalación
          </button>
          <button
            type="button"
            className={`installation-tab ${activeTab === "uninstallation" ? "active" : ""}`}
            onClick={() => setActiveTab("uninstallation")}
          >
            Desinstalación
          </button>
          <button
            type="button"
            className={`installation-tab ${activeTab === "history" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("history");
              loadHistory();
            }}
          >
            Historial ({history.length})
          </button>
        </nav>

        {activeTab !== "history" && (
          <form className="installation-card" onSubmit={handleSubmit}>
            <h2>{eventLabel(eventType)} del equipo</h2>
            <p className="installation-card-note">
              El PDF se genera automáticamente al guardar el registro.
            </p>

            {formError ? <div className="installation-alert error">{formError}</div> : null}
            {formMessage ? (
              <div className="installation-alert success">{formMessage}</div>
            ) : null}

            <div className="installation-grid">
              <div className="installation-field full">
                <label htmlFor="installation-location">Ubicación *</label>
                <input
                  id="installation-location"
                  className="installation-input"
                  value={form.location}
                  onChange={(event) => updateForm("location", event.target.value)}
                  placeholder="Ej.: Planta de tratamiento, área de laboratorio"
                  required
                />
              </div>

              <div className="installation-field">
                <label htmlFor="installation-date">
                  Fecha de {eventType === "installation" ? "instalación" : "desinstalación"} *
                </label>
                <input
                  id="installation-date"
                  type="date"
                  className="installation-input"
                  value={form.event_date}
                  onChange={(event) => updateForm("event_date", event.target.value)}
                  required
                />
              </div>

              <div className="installation-field">
                <label htmlFor="installation-time">Hora *</label>
                <input
                  id="installation-time"
                  type="time"
                  className="installation-input"
                  value={form.event_time}
                  onChange={(event) => updateForm("event_time", event.target.value)}
                  required
                />
              </div>

              <div className="installation-field full">
                <label htmlFor="installation-responsible">Encargados *</label>
                <input
                  id="installation-responsible"
                  className="installation-input"
                  value={form.responsible_people}
                  onChange={(event) =>
                    updateForm("responsible_people", event.target.value)
                  }
                  placeholder="Nombres de los técnicos y responsables"
                  required
                />
              </div>

              <div className="installation-field">
                <label htmlFor="installation-latitude">Latitud</label>
                <input
                  id="installation-latitude"
                  className="installation-input"
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(event) => updateForm("latitude", event.target.value)}
                  placeholder="-2.15652"
                />
              </div>

              <div className="installation-field">
                <label htmlFor="installation-longitude">Longitud</label>
                <input
                  id="installation-longitude"
                  className="installation-input"
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(event) => updateForm("longitude", event.target.value)}
                  placeholder="-79.89475"
                />
              </div>

              {eventType === "uninstallation" && (
                <div className="installation-field full">
                  <label htmlFor="installation-reason">Motivo de desinstalación *</label>
                  <textarea
                    id="installation-reason"
                    className="installation-textarea"
                    value={form.reason}
                    onChange={(event) => updateForm("reason", event.target.value)}
                    placeholder="Indica por qué se retira el equipo"
                    required
                  />
                </div>
              )}

              <div className="installation-field full">
                <label htmlFor="installation-observations">Observaciones</label>
                <textarea
                  id="installation-observations"
                  className="installation-textarea"
                  value={form.observations}
                  onChange={(event) => updateForm("observations", event.target.value)}
                  placeholder="Condiciones del sitio, conexiones, novedades, recomendaciones..."
                />
              </div>
            </div>

            <div className="installation-section-title">
              <h3>Comprobación metrológica</h3>
              <button
                type="button"
                className="installation-secondary-button"
                onClick={addCheck}
              >
                + Agregar parámetro
              </button>
            </div>

            <div className="installation-check-wrap">
              <table className="installation-check-table">
                <thead>
                  <tr>
                    <th>Parámetro</th>
                    <th>Unidad</th>
                    <th>Valor de referencia</th>
                    <th>Valor medido</th>
                    <th>Tolerancia</th>
                    <th>Error</th>
                    <th>Cumple</th>
                    <th>Observación</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((check, index) => {
                    const result = calculateCheck(check);
                    return (
                      <tr key={`check-${index}`}>
                        <td>
                          <input
                            className="installation-input"
                            value={check.parameter_name}
                            onChange={(event) =>
                              updateCheck(index, "parameter_name", event.target.value)
                            }
                            placeholder="Ej.: pH"
                          />
                        </td>
                        <td>
                          <input
                            className="installation-input"
                            value={check.unit}
                            onChange={(event) =>
                              updateCheck(index, "unit", event.target.value)
                            }
                            placeholder="pH"
                          />
                        </td>
                        <td>
                          <input
                            className="installation-input"
                            type="number"
                            step="any"
                            value={check.reference_value}
                            onChange={(event) =>
                              updateCheck(index, "reference_value", event.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="installation-input"
                            type="number"
                            step="any"
                            value={check.measured_value}
                            onChange={(event) =>
                              updateCheck(index, "measured_value", event.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="installation-input"
                            type="number"
                            step="any"
                            value={check.tolerance}
                            onChange={(event) =>
                              updateCheck(index, "tolerance", event.target.value)
                            }
                          />
                        </td>
                        <td>
                          <div className="installation-result pending">
                            {result.error === null ? "—" : formatNumber(result.error)}
                          </div>
                        </td>
                        <td>
                          <div
                            className={`installation-result ${
                              result.compliance === true
                                ? "ok"
                                : result.compliance === false
                                ? "no"
                                : "pending"
                            }`}
                          >
                            {result.compliance === true
                              ? "SÍ"
                              : result.compliance === false
                              ? "NO"
                              : "—"}
                          </div>
                        </td>
                        <td>
                          <input
                            className="installation-input"
                            value={check.observations}
                            onChange={(event) =>
                              updateCheck(index, "observations", event.target.value)
                            }
                            placeholder="Opcional"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="installation-remove"
                            onClick={() => removeCheck(index)}
                            disabled={checks.length === 1}
                            title="Eliminar parámetro"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="installation-section-title">
              <h3>Evidencias fotográficas</h3>
              <span>{evidences.length} de {MAX_EVIDENCES}</span>
            </div>

            <label className="installation-upload">
              Seleccionar o tomar fotografías JPG/PNG — máximo 5 MB por imagen
              <input
                type="file"
                accept="image/jpeg,image/png"
                multiple
                onChange={handleEvidenceSelection}
              />
            </label>

            {evidences.length > 0 && (
              <div className="installation-evidence-grid">
                {evidences.map((item, index) => (
                  <div className="installation-evidence-card" key={item.id}>
                    <img src={item.preview} alt={`Evidencia ${index + 1}`} />
                    <input
                      className="installation-input"
                      value={item.description}
                      onChange={(event) =>
                        updateEvidenceDescription(index, event.target.value)
                      }
                      placeholder="Descripción de la evidencia"
                    />
                    <div className="installation-evidence-actions">
                      <span>{(item.file.size / 1024 / 1024).toFixed(2)} MB</span>
                      <button
                        type="button"
                        className="installation-remove"
                        onClick={() => removeEvidence(index)}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="installation-submit-row">
              <button className="installation-submit" type="submit" disabled={saving}>
                {saving
                  ? "Guardando y generando PDF..."
                  : `Guardar ${eventLabel(eventType).toLowerCase()}`}
              </button>
            </div>
          </form>
        )}

        {activeTab === "history" && (
          <section className="installation-card">
            <h2>Historial del equipo</h2>
            <p className="installation-card-note">
              Cada evento conserva sus propios datos, comprobaciones, fotografías y PDF.
            </p>

            {formMessage ? (
              <div className="installation-alert success">{formMessage}</div>
            ) : null}
            {historyError ? (
              <div className="installation-alert error">{historyError}</div>
            ) : null}

            <div className="installation-history-grid">
              <div className="installation-state-card">
                <h3>Última instalación</h3>
                {latestInstallation ? (
                  <>
                    <p><strong>Fecha:</strong> {formatDate(latestInstallation.event_date)} {formatTime(latestInstallation.event_time)}</p>
                    <p><strong>Ubicación:</strong> {latestInstallation.location}</p>
                    <p><strong>Encargados:</strong> {latestInstallation.responsible_people}</p>
                  </>
                ) : (
                  <p>No existe una instalación registrada.</p>
                )}
              </div>
              <div className="installation-state-card">
                <h3>Última desinstalación</h3>
                {latestUninstallation ? (
                  <>
                    <p><strong>Fecha:</strong> {formatDate(latestUninstallation.event_date)} {formatTime(latestUninstallation.event_time)}</p>
                    <p><strong>Ubicación:</strong> {latestUninstallation.location}</p>
                    <p><strong>Motivo:</strong> {latestUninstallation.reason || "—"}</p>
                  </>
                ) : (
                  <p>No existe una desinstalación registrada.</p>
                )}
              </div>
            </div>

            {historyLoading ? (
              <div className="installation-empty">Cargando historial...</div>
            ) : history.length === 0 ? (
              <div className="installation-empty">Todavía no hay eventos registrados.</div>
            ) : (
              <div className="installation-history-table-wrap">
                <table className="installation-history-table">
                  <thead>
                    <tr>
                      <th>Evento</th>
                      <th>Fecha y hora</th>
                      <th>Ubicación</th>
                      <th>Encargados</th>
                      <th>Evidencias</th>
                      <th>Comprobaciones</th>
                      <th>PDF</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <span className="installation-pill">
                            {eventLabel(item.event_type)}
                          </span>
                        </td>
                        <td>{formatDate(item.event_date)} {formatTime(item.event_time)}</td>
                        <td>{item.location}</td>
                        <td>{item.responsible_people}</td>
                        <td>{item.evidence_count ?? 0}</td>
                        <td>{item.check_count ?? 0}</td>
                        <td>{item.pdf_status === "generated" ? "Generado" : item.pdf_status === "error" ? "Error" : "Pendiente"}</td>
                        <td>
                          <div className="installation-action-row">
                            <button
                              type="button"
                              className="installation-action"
                              onClick={() => openDetail(item.id)}
                              disabled={detailLoading}
                            >
                              Ver detalle
                            </button>
                            {item.pdf_url ? (
                              <a
                                className="installation-action pdf"
                                href={resolvePublicUrl(item.pdf_url)}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Ver PDF
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="installation-action warning"
                                onClick={() => regeneratePdf(item.id)}
                                disabled={regeneratingId === item.id}
                              >
                                {regeneratingId === item.id ? "Generando..." : "Regenerar PDF"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      {detail && (
        <div className="installation-modal" role="dialog" aria-modal="true" onClick={() => setDetail(null)}>
          <div className="installation-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="installation-modal-head">
              <h2>{eventLabel(detail.event_type)} — evento #{detail.id}</h2>
              <button className="installation-close" type="button" onClick={() => setDetail(null)}>
                Cerrar
              </button>
            </div>

            <div className="installation-detail-grid">
              <div className="installation-detail-item"><span>Ubicación</span><strong>{detail.location || "—"}</strong></div>
              <div className="installation-detail-item"><span>Fecha y hora</span><strong>{formatDate(detail.event_date)} {formatTime(detail.event_time)}</strong></div>
              <div className="installation-detail-item"><span>Encargados</span><strong>{detail.responsible_people || "—"}</strong></div>
              <div className="installation-detail-item"><span>Estado</span><strong>{statusLabel(detail.status)}</strong></div>
              <div className="installation-detail-item"><span>Coordenadas</span><strong>{detail.latitude ?? "—"}, {detail.longitude ?? "—"}</strong></div>
              <div className="installation-detail-item"><span>Registrado por</span><strong>{detail.created_by_name || detail.created_by_username || "—"}</strong></div>
              {detail.event_type === "uninstallation" && (
                <div className="installation-detail-item"><span>Motivo</span><strong>{detail.reason || "—"}</strong></div>
              )}
              <div className="installation-detail-item"><span>Observaciones</span><strong>{detail.observations || "—"}</strong></div>
            </div>

            <div className="installation-section-title"><h3>Comprobación metrológica</h3></div>
            <div className="installation-check-wrap">
              <table className="installation-check-table">
                <thead><tr><th>Parámetro</th><th>Unidad</th><th>Referencia</th><th>Medido</th><th>Error</th><th>Tolerancia</th><th>Cumple</th><th>Observación</th></tr></thead>
                <tbody>
                  {(detail.checks || []).length ? (detail.checks || []).map((check) => (
                    <tr key={check.id}>
                      <td>{check.parameter_name}</td><td>{check.unit || "—"}</td><td>{formatNumber(check.reference_value)}</td><td>{formatNumber(check.measured_value)}</td><td>{formatNumber(check.error_value)}</td><td>{formatNumber(check.tolerance)}</td><td>{check.compliance === true ? "SÍ" : check.compliance === false ? "NO" : "—"}</td><td>{check.observations || "—"}</td>
                    </tr>
                  )) : <tr><td colSpan="8">No se registraron comprobaciones.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="installation-section-title"><h3>Evidencias</h3></div>
            {(detail.evidences || []).length ? (
              <div className="installation-detail-images">
                {(detail.evidences || []).map((evidence, index) => (
                  <figure key={evidence.id}>
                    <a href={resolvePublicUrl(evidence.file_url)} target="_blank" rel="noreferrer">
                      <img src={resolvePublicUrl(evidence.file_url)} alt={`Evidencia ${index + 1}`} />
                    </a>
                    <figcaption>{evidence.description || evidence.original_filename || `Evidencia ${index + 1}`}</figcaption>
                  </figure>
                ))}
              </div>
            ) : <div className="installation-empty">No se registraron evidencias.</div>}

            <div className="installation-submit-row">
              {detail.pdf_url ? (
                <a className="installation-action pdf" href={resolvePublicUrl(detail.pdf_url)} target="_blank" rel="noreferrer">Abrir PDF</a>
              ) : (
                <button type="button" className="installation-action warning" onClick={() => regeneratePdf(detail.id)} disabled={regeneratingId === detail.id}>
                  {regeneratingId === detail.id ? "Generando..." : "Regenerar PDF"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
